import {Icon, Tooltip, view} from '@sail/ui';
import {warning} from '@sail/icons/react/Icon';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {ChatRowContainer} from './ChatRowContainer';
import type {TextBubble} from './TextBubble';

type Props = {
  position?: 'start' | 'end';
  avatar?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  messageType?: React.ComponentProps<typeof TextBubble>['messageType'];
};

const ErrorMessage = ({children}: {children: React.ReactNode}): JSX.Element => {
  return (
    <Tooltip
      placement="left"
      delay={0}
      trigger={
        <Icon
          icon={warning}
          aria-label="warning"
          data-testid="error-tooltip-icon"
          css={{
            fill: 'red',
          }}
        />
      }
    >
      {children}
    </Tooltip>
  );
};

export const ChatRow = ({
  children,
  position = 'start',
  avatar,
  error,
  messageType,
}: Props): JSX.Element => {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  /* eslint-disable no-use-before-define */
  if (useNewDesign) {
    return (
      <ChatRowNext position={position} error={error} messageType={messageType}>
        {children}
      </ChatRowNext>
    );
  }
  /* eslint-enable no-use-before-define */

  const innerNodes = [
    error ? <ErrorMessage key="error">{error}</ErrorMessage> : null,
    avatar ? <view.div key="avatar">{avatar}</view.div> : null,
    children,
  ];

  const largeMargin = useDotComDesign ? 'xxlarge' : 'large';

  return (
    <view.div
      data-testid="chat-row"
      css={{
        marginLeft: position === 'start' ? 0 : largeMargin,
        marginRight: position === 'start' ? largeMargin : 0,
      }}
    >
      <ChatRowContainer position={position}>
        {position === 'start' ? innerNodes : innerNodes.reverse()}
      </ChatRowContainer>
    </view.div>
  );
};

const ChatRowNext = ({
  children,
  position = 'start',
  error,
  messageType,
}: Omit<Props, 'avatar'>): JSX.Element => {
  const errorNode = error ? (
    <ErrorMessage key="error">{error}</ErrorMessage>
  ) : null;

  return (
    <view.div
      className="uh-conversations-chat-row"
      data-testid="chat-row"
      data-message={messageType}
      css={{
        stack: 'x',
        alignX: position,
        gap: 'small',
        marginLeft: position === 'start' ? 0 : 'large',
      }}
    >
      {position === 'start' ? errorNode : null}
      {children}
      {position !== 'start' ? errorNode : null}
    </view.div>
  );
};
